import React, { useEffect, useState } from "react";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import SingleSelect from "components/common/SingleSelect";
import CustomField from "components/form/CustomField";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { branchCameraValidationSchema, storeCameraValidationSchema } from "utils/validations";
import { handleSubmitForm } from "helpers/apiUtils";
import { useNavigate } from "react-router-dom";
import { usePermission } from "hooks/usePermission";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import { CAMERA_ROLE_TYPE, LANE_OPTIONS } from "utils/constants";
import { addStoreCamera, updateStoreCamera } from "store/slices/storeCameraSlice";
import {
  getBranchCameraDetails,
  clearItemDetails,
} from "store/slices/branchCameraSlice";


const AddEditBranchCamera = ({ mode }) => {
  const { t } = useTranslation();
  const isEdit = mode === "edit";
  const branchId = useQueryParam("branchId");
  const id = useQueryParam("id");
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("developer_settings");

  const { itemDetails: cameraDetails, loading } = useSelector(
    (state) => state.BranchCamera,
  );

  const [initialValues, setInitialValues] = useState({
    name: "",
    cameraId: "",
    role: "",
    lane: "",
  });

  useEffect(() => {
    if (isEdit && branchId && id) {
      dispatch(getBranchCameraDetails({ branchId, id }));
    }
    return () => dispatch(clearItemDetails());
  }, [isEdit, branchId, id, dispatch]);

  useEffect(() => {
    if (isEdit && cameraDetails) {
      const { cameraId, name, role, lane } = cameraDetails || {};
      setInitialValues({
        name: name || "",
        cameraId: cameraId || "",
        role: role || "",
        lane: lane || "",
      });
    }
  }, [isEdit, cameraDetails]);

  const handleSubmit = (values) => {
    const camera = {
      ...(isEdit && { id }),
      cameraId: values.cameraId,
      name: values.name,
      role: values.role,
      lane: values.lane,
      meta: {},
    };

    handleSubmitForm({
      dispatch,
      action: isEdit ? updateStoreCamera : addStoreCamera,
      payload: {
        branchId,
        cameras: [camera],
      },
      onSuccess: () => navigate(-1),
    });
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading)
    return <AddEditLoader title={t("Edit Camera")} rows={2} cols={3} />;

  return (
    <div className="page-content add_camera_page">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Camera") : t("Add Camera")}
      />
      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={branchCameraValidationSchema(t)}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Camera id / ANPR device id")}
                      name="cameraId"
                      placeholder={t("Enter ANPR device id / camera id")}
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name")}
                      name="name"
                      placeholder={t("Enter name")}
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <label>
                      {t("Lane")}
                      <span className="text-danger">*</span>
                    </label>
                    <SingleSelect
                      value={values.lane || ""}
                      options={LANE_OPTIONS}
                      handleSingleSelectChange={(option) =>
                        setFieldValue("lane", option?.value || "")
                      }
                      error={errors.lane && touched.lane ? errors.lane : null}
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <label>
                      {t("Role")}
                      <span className="text-danger">*</span>
                    </label>
                    <SingleSelect
                      value={values.role}
                      options={CAMERA_ROLE_TYPE}
                      handleSingleSelectChange={(e) => {
                        setFieldValue("role", e.value);
                      }}
                      error={errors.role && touched.role ? errors.role : null}
                    />
                  </div>
                </Col>

                <Col lg={12} className="mt-3">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="main_btn outline"
                      onClick={goBack}
                    >
                      {t("Cancel")}
                    </button>

                    <button type="submit" className="main_btn">
                      {isEdit ? t("Save Changes") : t("Create Camera")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditBranchCamera;
